import { Region, AmbientSound, MusicTrack } from "../types";

// ─── Shared ambient sounds available at every location ─────────────────────

const UNIVERSAL_AMBIENTS: AmbientSound[] = [
  {
    id: "u-chanting",
    label: "Chanting",
    icon: "🙏",
    file: "chanting.mp3",
    loop: true,
  },
  {
    id: "u-churchbell",
    label: "Church Bell",
    icon: "🔔",
    file: "churchbell.mp3",
    loop: false,
  },
  {
    id: "u-darkness",
    label: "Darkness",
    icon: "🌑",
    file: "darkness.mp3",
    loop: true,
  },
  {
    id: "u-demon-chanting",
    label: "Demon Chanting",
    icon: "👹",
    file: "demon_chanting.mp3",
    loop: true,
  },
  {
    id: "u-evil-spirit",
    label: "Evil Spirit",
    icon: "👻",
    file: "evil_spirit.mp3",
    loop: true,
  },
  {
    id: "u-footsteps",
    label: "Footsteps",
    icon: "👣",
    file: "footsteps.mp3",
    loop: true,
  },
  { id: "u-ghost", label: "Ghost", icon: "💀", file: "ghost.mp3", loop: true },
  {
    id: "u-heartbeat",
    label: "Heartbeat",
    icon: "❤️",
    file: "heartbeat.mp3",
    loop: true,
  },
  {
    id: "u-inception",
    label: "Inception",
    icon: "🌀",
    file: "inception.mp3",
    loop: true,
  },
  {
    id: "u-mechanism",
    label: "Mechanism Opening",
    icon: "⚙️",
    file: "mechanism_opening.mp3",
    loop: false,
  },
  {
    id: "u-on-fire",
    label: "On Fire",
    icon: "🔥",
    file: "on_fire.mp3",
    loop: true,
  },
  {
    id: "u-thunderstorm",
    label: "Thunderstorm",
    icon: "⛈️",
    file: "thunderstorm.mp3",
    loop: true,
  },
  {
    id: "u-warhorn",
    label: "War Horn",
    icon: "📯",
    file: "warhorn.mp3",
    loop: false,
  },
  { id: "u-wind", label: "Wind", icon: "💨", file: "wind.mp3", loop: true },
];

// ─── Extra ambient sound definitions ─────────────────────────────────────────
const A: Record<string, AmbientSound> = {
  atSea: {
    id: "at_sea",
    label: "At Sea",
    icon: "⚓",
    file: "at_sea.mp3",
    loop: true,
  },
  cave: { id: "cave", label: "Cave", icon: "🕳️", file: "cave.mp3", loop: true },
  city: { id: "city", label: "City", icon: "🏙️", file: "city.mp3", loop: true },
  crowds: {
    id: "crowds",
    label: "Crowds",
    icon: "👥",
    file: "crowds.mp3",
    loop: true,
  },
  docks: {
    id: "docks",
    label: "Docks",
    icon: "🚢",
    file: "docks.mp3",
    loop: true,
  },
  fireplace: {
    id: "fireplace",
    label: "Fireplace",
    icon: "🔥",
    file: "fireplace.mp3",
    loop: true,
  },
  forest: {
    id: "forest_amb",
    label: "Forest",
    icon: "🌲",
    file: "forest.mp3",
    loop: true,
  },
  loadingGoods: {
    id: "loading_goods",
    label: "Loading Goods",
    icon: "📦",
    file: "loading_goods.mp3",
    loop: true,
  },
  market: {
    id: "market",
    label: "Market",
    icon: "🛒",
    file: "market.mp3",
    loop: true,
  },
  ship: {
    id: "ship_mp3",
    label: "Ship",
    icon: "🚢",
    file: "ship.mp3",
    loop: true,
  },
  underwater: {
    id: "underwater",
    label: "Underwater",
    icon: "🌊",
    file: "underwater.mp3",
    loop: true,
  },
  village: {
    id: "village_amb",
    label: "Village",
    icon: "🏡",
    file: "village.mp3",
    loop: true,
  },
  waterDrips: {
    id: "water_drips",
    label: "Water Drips",
    icon: "💧",
    file: "water_drips.mp3",
    loop: true,
  },
  waves: {
    id: "waves",
    label: "Waves",
    icon: "🌊",
    file: "waves.mp3",
    loop: true,
  },
};

// ─── Temple music shared across multiple locations ────────────────────────────
const TEMPLE_MUSIC: MusicTrack[] = [
  {
    id: "explore-temple-evil",
    label: "Explore · Evil",
    file: "temple_evil.mp3",
    type: "explore",
  },
  {
    id: "explore-temple-good",
    label: "Explore · Good",
    file: "temple_good.mp3",
    type: "explore",
  },
  {
    id: "explore-temple-generic",
    label: "Explore · Generic",
    file: "temple_generic.mp3",
    type: "explore",
  },
  {
    id: "combat-overwhelming",
    label: "Combat",
    file: "overwelming_battle.mp3",
    type: "combat",
  },
  { id: "victory", label: "Victory", file: "victory.mp3", type: "victory" },
];

const VICTORY: MusicTrack = {
  id: "victory",
  label: "Victory",
  file: "victory.mp3",
  type: "victory",
};

// ─── REGIONS ──────────────────────────────────────────────────────────────────
export const REGIONS: Region[] = [
  {
    id: "phlan",
    name: "Phlan",
    emoji: "⚔️",
    accentColor: "#e05c5c",
    locations: [
      {
        id: "phlan-final-battle",
        name: "Final Battle",
        emoji: "⚔️",
        gradient:
          "linear-gradient(135deg, #1a0000 0%, #4a0000 50%, #8b0000 100%)",
        accentColor: "#ff4444",
        musicTracks: [
          {
            id: "explore-oppressive",
            label: "Explore",
            file: "oppressive.mp3",
            type: "explore",
          },
          {
            id: "combat-large-battle",
            label: "Combat",
            file: "final_boss.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS],
      },
    ],
  },
  {
    id: "melvaunt",
    name: "Melvaunt",
    emoji: "🏙️",
    accentColor: "#7ab3e8",
    locations: [
      // Mines
      {
        id: "old-mines",
        name: "Old Mines",
        emoji: "⛏️",
        gradient:
          "linear-gradient(135deg, #0a0810 0%, #1f1535 50%, #2d1f4a 100%)",
        accentColor: "#8b7fb8",
        musicTracks: [
          {
            id: "explore-ruins",
            label: "Explore",
            file: "ruins.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.cave, A.waterDrips],
      },
      {
        id: "new-mines",
        name: "New Mines",
        emoji: "⛏️",
        gradient:
          "linear-gradient(135deg, #0a0810 0%, #1f1535 50%, #2d1f4a 100%)",
        accentColor: "#9090d0",
        musicTracks: [
          {
            id: "explore-castle",
            label: "Explore",
            file: "castle_exploring.mp3",
            type: "explore",
          },
          {
            id: "combat-timed",
            label: "Combat",
            file: "timed_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.cave, A.waterDrips],
      },
      // Shops
      {
        id: "blackalbucks",
        name: "Blackalbuck's Swap Shop",
        emoji: "🔄",
        gradient:
          "linear-gradient(135deg, #0d2610 0%, #1a5c2a 50%, #3d8c3d 100%)",
        accentColor: "#7ecf7e",
        musicTracks: [
          {
            id: "explore-forest",
            label: "Explore",
            file: "forest.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.loadingGoods, A.market],
      },
      {
        id: "ganzas-arms",
        name: "Ganza's Arms & Armor",
        emoji: "⚔️",
        gradient:
          "linear-gradient(135deg, #1a1a1a 0%, #3d3d3d 50%, #6b6b6b 100%)",
        accentColor: "#c0c0c0",
        musicTracks: [
          {
            id: "explore-forest",
            label: "Explore",
            file: "forest.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.loadingGoods],
      },
      {
        id: "hermuks",
        name: "Hermuk's Allgoods",
        emoji: "🛍️",
        gradient:
          "linear-gradient(135deg, #1a1f00 0%, #3d4a00 50%, #6b7a00 100%)",
        accentColor: "#c8d44a",
        musicTracks: [
          {
            id: "explore-exotic",
            label: "Explore",
            file: "exotic.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS],
      },
      {
        id: "markov-steelworks",
        name: "Markov Steelworks",
        emoji: "🔨",
        gradient:
          "linear-gradient(135deg, #1a0f00 0%, #4a2d00 50%, #7a5000 100%)",
        accentColor: "#d4804a",
        musicTracks: [
          {
            id: "explore-forest",
            label: "Explore",
            file: "forest.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.loadingGoods],
      },
      {
        id: "market-center",
        name: "Market Center",
        emoji: "🛒",
        gradient:
          "linear-gradient(135deg, #1a1200 0%, #4a3800 50%, #8a6800 100%)",
        accentColor: "#e8c84a",
        musicTracks: [
          {
            id: "explore-forest",
            label: "Explore",
            file: "forest.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [
          ...UNIVERSAL_AMBIENTS,
          A.city,
          A.market,
          A.loadingGoods,
        ],
      },
      // Taverns & Inns
      {
        id: "breakwater-inn",
        name: "Breakwater Inn",
        emoji: "🍺",
        gradient:
          "linear-gradient(135deg, #3d1a0a 0%, #7a3b1e 50%, #c4721f 100%)",
        accentColor: "#e8a84c",
        musicTracks: [
          {
            id: "explore-happy-tavern",
            label: "Explore",
            file: "happy_tavern.mp3",
            type: "explore",
          },
          {
            id: "combat-tavern",
            label: "Combat",
            file: "tavern_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.crowds, A.fireplace],
      },
      {
        id: "deils-fire",
        name: "Deil's Fire Tavern",
        emoji: "🔥",
        gradient:
          "linear-gradient(135deg, #2a0800 0%, #6b2200 50%, #c44a00 100%)",
        accentColor: "#ff7a30",
        musicTracks: [
          {
            id: "explore-happy-tavern",
            label: "Explore",
            file: "happy_tavern.mp3",
            type: "explore",
          },
          {
            id: "combat-tavern",
            label: "Combat",
            file: "tavern_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.crowds, A.fireplace],
      },
      {
        id: "floating-fighter",
        name: "Floating Fighter",
        emoji: "🚢",
        gradient:
          "linear-gradient(135deg, #001a33 0%, #003d6b 50%, #006bb3 100%)",
        accentColor: "#4db8ff",
        musicTracks: [
          {
            id: "explore-exotic",
            label: "Explore",
            file: "exotic.mp3",
            type: "explore",
          },
          {
            id: "combat-tavern",
            label: "Combat",
            file: "tavern_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.crowds, A.fireplace],
      },
      {
        id: "umberlee",
        name: "Umberlee's Bosom",
        emoji: "🌊",
        gradient:
          "linear-gradient(135deg, #001a2a 0%, #003d5c 50%, #006b8a 100%)",
        accentColor: "#30c8d4",
        musicTracks: [
          {
            id: "explore-happy-tavern",
            label: "Explore",
            file: "happy_tavern.mp3",
            type: "explore",
          },
          {
            id: "combat-tavern",
            label: "Combat",
            file: "tavern_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.crowds, A.fireplace, A.waves],
      },
      // Temples
      {
        id: "laughing-halls",
        name: "Laughing Halls",
        emoji: "😂",
        gradient:
          "linear-gradient(135deg, #0f1f1f 0%, #1a4a3d 50%, #2d7a6b 100%)",
        accentColor: "#5ecfb8",
        musicTracks: TEMPLE_MUSIC,
        ambientSounds: [...UNIVERSAL_AMBIENTS],
      },
      {
        id: "purple-portals",
        name: "Purple Portals",
        emoji: "🟣",
        gradient:
          "linear-gradient(135deg, #1a0a2e 0%, #3d1a6b 50%, #6b3ab3 100%)",
        accentColor: "#c07aff",
        musicTracks: TEMPLE_MUSIC,
        ambientSounds: [...UNIVERSAL_AMBIENTS],
      },
      {
        id: "shrine-sharess",
        name: "Shrine to Sharess",
        emoji: "🌸",
        gradient:
          "linear-gradient(135deg, #2a0a1a 0%, #6b1a4a 50%, #b33a7a 100%)",
        accentColor: "#ff7ab8",
        musicTracks: TEMPLE_MUSIC,
        ambientSounds: [...UNIVERSAL_AMBIENTS],
      },
      {
        id: "shrine-tempus",
        name: "Shrine to Tempus",
        emoji: "🗡️",
        gradient:
          "linear-gradient(135deg, #1a0000 0%, #4a1010 50%, #8b3030 100%)",
        accentColor: "#e06060",
        musicTracks: TEMPLE_MUSIC,
        ambientSounds: [...UNIVERSAL_AMBIENTS],
      },
      {
        id: "shrine-tymora",
        name: "Shrine to Tymora",
        emoji: "🍀",
        gradient:
          "linear-gradient(135deg, #0a1a00 0%, #1a4a00 50%, #3a8a00 100%)",
        accentColor: "#7ad44a",
        musicTracks: TEMPLE_MUSIC,
        ambientSounds: [...UNIVERSAL_AMBIENTS],
      },
      // Districts & other areas
      {
        id: "bruilhaven",
        name: "Bruilhaven",
        emoji: "🏰",
        gradient:
          "linear-gradient(135deg, #1a1a1a 0%, #3d3d3d 50%, #6b6b6b 100%)",
        accentColor: "#d0d0d0",
        musicTracks: [
          {
            id: "explore-castle",
            label: "Explore",
            file: "castle_exploring.mp3",
            type: "explore",
          },
          {
            id: "combat-overwhelming",
            label: "Combat",
            file: "overwelming_battle.mp3",
            type: "combat",
          },
          {
            id: "combat-boss",
            label: "Combat · Boss",
            file: "large_single_boss_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.city],
      },
      {
        id: "leiyraghon",
        name: "Leiyraghon",
        emoji: "🌿",
        gradient:
          "linear-gradient(135deg, #0d2610 0%, #1a5c2a 50%, #3d8c3d 100%)",
        accentColor: "#7ecf7e",
        musicTracks: [
          {
            id: "explore-happy-tavern",
            label: "Explore",
            file: "happy_tavern.mp3",
            type: "explore",
          },
          {
            id: "combat-tavern",
            label: "Combat",
            file: "tavern_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.crowds, A.forest, A.city],
      },
      {
        id: "nanthar-keep",
        name: "Nanthar Keep",
        emoji: "🏯",
        gradient:
          "linear-gradient(135deg, #1a1a2a 0%, #2d2d4a 50%, #4a4a7a 100%)",
        accentColor: "#9090d0",
        musicTracks: [
          {
            id: "explore-wilds",
            label: "Explore",
            file: "wilds.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          {
            id: "combat-final-boss",
            label: "Combat · Final Boss",
            file: "final_boss.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.crowds, A.cave, A.waterDrips],
      },
      {
        id: "asberyth",
        name: "Asberyth",
        emoji: "🌑",
        gradient:
          "linear-gradient(135deg, #0a0a0a 0%, #1a1520 50%, #2a2035 100%)",
        accentColor: "#8060a0",
        musicTracks: [
          {
            id: "explore-oppressive",
            label: "Explore",
            file: "oppressive.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          {
            id: "combat-final-boss",
            label: "Combat · Final Boss",
            file: "final_boss.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.crowds],
      },
      {
        id: "docks",
        name: "Docks",
        emoji: "⚓",
        gradient:
          "linear-gradient(135deg, #001020 0%, #002040 50%, #004080 100%)",
        accentColor: "#4090c0",
        musicTracks: [
          {
            id: "explore-forest",
            label: "Explore",
            file: "forest.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [
          ...UNIVERSAL_AMBIENTS,
          A.docks,
          A.loadingGoods,
          A.market,
          A.crowds,
          A.ship,
          A.underwater,
        ],
      },
      {
        id: "crimson-chain",
        name: "Crimson Chain Hideout",
        emoji: "⛓️",
        gradient:
          "linear-gradient(135deg, #1a0000 0%, #3a0808 50%, #5a1010 100%)",
        accentColor: "#c04040",
        musicTracks: [
          {
            id: "explore-ruins",
            label: "Explore",
            file: "ruins.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.cave, A.waves, A.underwater],
      },
      {
        id: "shipyards",
        name: "Shipyards",
        emoji: "🔧",
        gradient:
          "linear-gradient(135deg, #001520 0%, #002a40 50%, #004a6a 100%)",
        accentColor: "#30a0c0",
        musicTracks: [
          {
            id: "explore-forest",
            label: "Explore",
            file: "forest.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [
          ...UNIVERSAL_AMBIENTS,
          A.docks,
          A.loadingGoods,
          A.market,
          A.crowds,
          A.ship,
          A.underwater,
        ],
      },
      {
        id: "melvaunt-jail",
        name: "Melvaunt Jail",
        emoji: "🔒",
        gradient:
          "linear-gradient(135deg, #0f0f0f 0%, #1a1a2a 50%, #252540 100%)",
        accentColor: "#6060a0",
        musicTracks: [
          {
            id: "explore-oppressive",
            label: "Explore",
            file: "oppressive.mp3",
            type: "explore",
          },
          {
            id: "combat-general",
            label: "Combat",
            file: "general_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.waterDrips, A.cave],
      },
      {
        id: "travelling-city",
        name: "Travelling Through the City",
        emoji: "🚶",
        gradient:
          "linear-gradient(135deg, #1a1a2e 0%, #2d2d4a 50%, #4a4a6a 100%)",
        accentColor: "#8080b0",
        musicTracks: [
          {
            id: "explore-forest",
            label: "Explore",
            file: "forest.mp3",
            type: "explore",
          },
          VICTORY,
        ],
        ambientSounds: [
          ...UNIVERSAL_AMBIENTS,
          A.city,
          A.crowds,
          A.market,
          A.docks,
          A.loadingGoods,
          A.village,
          A.waves,
          A.underwater,
        ],
      },
      {
        id: "basement",
        name: "Basement",
        emoji: "🪜",
        gradient:
          "linear-gradient(135deg, #080810 0%, #10101a 50%, #181828 100%)",
        accentColor: "#5050a0",
        musicTracks: [
          {
            id: "explore-ruins",
            label: "Explore",
            file: "ruins.mp3",
            type: "explore",
          },
          {
            id: "combat-horror",
            label: "Combat",
            file: "horror_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.cave, A.waterDrips],
      },
      {
        id: "under-temple",
        name: "Under Temple",
        emoji: "🕍",
        gradient:
          "linear-gradient(135deg, #0f1f1f 0%, #1a3a3a 50%, #2a5050 100%)",
        accentColor: "#5ec8b0",
        musicTracks: [
          {
            id: "explore-ruins",
            label: "Explore",
            file: "ruins.mp3",
            type: "explore",
          },
          {
            id: "combat-overwhelming",
            label: "Combat",
            file: "overwelming_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS],
      },
      {
        id: "sailing",
        name: "Sailing",
        emoji: "⛵",
        gradient:
          "linear-gradient(135deg, #001833 0%, #003060 50%, #005090 100%)",
        accentColor: "#40a0e0",
        musicTracks: [
          {
            id: "explore-tranquil",
            label: "Explore",
            file: "tranquil.mp3",
            type: "explore",
          },
          {
            id: "combat-timed",
            label: "Combat",
            file: "timed_battle.mp3",
            type: "combat",
          },
          VICTORY,
        ],
        ambientSounds: [...UNIVERSAL_AMBIENTS, A.atSea, A.ship, A.waves],
      },
    ],
  },
];
